/**
 * Wealthica v2 API Client (Refactored)
 * 
 * Lightweight facade orchestrating specialized services
 * Decomposed from 723-line monolith into focused, testable modules
 */

import { WealthicaAuthService } from './services/WealthicaAuthService';
import { WealthicaConnectionService } from './services/WealthicaConnectionService';
import { WealthicaDataService } from './services/WealthicaDataService';
import { WealthicaCallbackService } from './services/WealthicaCallbackService';
import { WealthicaErrorType } from './constants-v2';
import { WealthicaApiError } from './types';
import { logger } from './utils/logger';
import type { 
  WealthicaConnectionOptions,
  WealthicaCallbackParams,
  WealthicaConnectionResult,
  WealthicaCallbackResult
} from './types';

/**
 * Lightweight Wealthica API client that orchestrates specialized services
 */
export class Wealthica2Client {
  private baseUrl: string;
  private isInitialized: boolean = false;
  
  constructor() {
    this.baseUrl = 'https://api.wealthica.com'; // Will be updated with actual base URL
    this.isInitialized = true;
    
    logger.info('client', 'Wealthica2Client initialized with service-oriented architecture', { 
      baseUrl: this.baseUrl 
    });
  }

  /**
   * Connect to Wealthica
   * Delegates to WealthicaConnectionService
   */
  async connect(options: WealthicaConnectionOptions = {}): Promise<WealthicaConnectionResult> {
    try {
      logger.info('client.connect', 'Delegating connection to WealthicaConnectionService');
      return await WealthicaConnectionService.connect(options);
    } catch (error) {
      logger.error('client.connect', 'Connection failed', { error });
      return {
        success: false,
        error: this.normalizeError(error).message,
      };
    }
  }

  /**
   * Process callback from Wealthica
   * Delegates to WealthicaCallbackService
   */
  async processCallback(params?: WealthicaCallbackParams, userId?: string): Promise<WealthicaCallbackResult> {
    try {
      logger.info('client.processCallback', 'Delegating callback processing to WealthicaCallbackService');
      return await WealthicaCallbackService.processCallback(params, userId);
    } catch (error) {
      logger.error('client.processCallback', 'Callback processing failed', { error });
      return {
        success: false,
        error: this.normalizeError(error).message,
      };
    }
  }

  /**
   * Reconnect/refresh existing Wealthica account
   * Delegates to WealthicaDataService
   */
  async reconnect(options: { reference: string; institutionId: string }): Promise<{ 
    success: boolean; 
    status?: string; 
    message?: string; 
    data?: { accounts?: any[]; positions?: any[]; institution?: any; } 
  }> {
    try {
      logger.info('client.reconnect', 'Delegating account refresh to WealthicaDataService');
      return await WealthicaDataService.refreshAccounts(options);
    } catch (error) {
      logger.error('client.reconnect', 'Account refresh failed', { error });
      throw error;
    }
  }

  /**
   * Get Wealthica accounts for the current user
   * Delegates to WealthicaDataService
   */
  async getAccounts(bearerToken?: string): Promise<any[]> {
    try {
      logger.info('client.getAccounts', 'Delegating account fetching to WealthicaDataService');
      const result = await WealthicaDataService.getAccounts(bearerToken);
      return result.success ? (result.data || []) : [];
    } catch (error) {
      logger.error('client.getAccounts', 'Failed to get accounts', { error });
      return [];
    }
  }

  /**
   * Get positions for a specific account
   * Delegates to WealthicaDataService
   */
  async getPositionsByAccount(bearerToken: string, accountId: string): Promise<any[]> {
    try {
      logger.info('client.getPositionsByAccount', 'Delegating position fetching to WealthicaDataService');
      const result = await WealthicaDataService.getPositionsByAccount(bearerToken, accountId);
      return result.success ? (result.data || []) : [];
    } catch (error) {
      logger.error('client.getPositionsByAccount', 'Failed to get positions', { error });
      return [];
    }
  }

  /**
   * Get positions for a specific institution ID, optionally filtered by account IDs
   * Delegates to WealthicaDataService
   */
  async getPositionsByInstitution(bearerToken: string, institutionId: string, accountIds?: string[]): Promise<any[]> {
    try {
      logger.info('client.getPositionsByInstitution', 'Delegating position fetching to WealthicaDataService');
      const result = await WealthicaDataService.getPositionsByInstitution(bearerToken, institutionId, accountIds);
      return result.success ? (result.data || []) : [];
    } catch (error) {
      logger.error('client.getPositionsByInstitution', 'Failed to get positions', { error });
      return [];
    }
  }

  /**
   * Validate token format and characteristics
   * Delegates to WealthicaAuthService
   */
  private isTokenValid(token?: string): boolean {
    const validation = WealthicaAuthService.validateToken(token);
    return validation.valid;
  }

  /**
   * Check if error is related to rate limiting
   * Delegates to WealthicaAuthService
   */
  private isRateLimitError(error: unknown): boolean {
    return WealthicaAuthService.isRateLimitError(error);
  }

  /**
   * Normalize errors to consistent format
   * Enhanced with service-based error classification
   */
  private normalizeError(error: unknown): WealthicaApiError {
    // Already normalized
    if (error instanceof WealthicaApiError) {
      return error;
    }
    
    // Check for rate limit errors using AuthService
    if (WealthicaAuthService.isRateLimitError(error)) {
      logger.warn('client.normalizeError', 'Rate limit detected', { error });
      return new WealthicaApiError(
        WealthicaErrorType.RATE_LIMIT_EXCEEDED,
        'Too many requests. Please try again in a few moments.'
      );
    }

    // Error with message
    if (error instanceof Error) {
      // Check for token/auth errors using AuthService
      if (WealthicaAuthService.isTokenError(error)) {
        return new WealthicaApiError(
          WealthicaErrorType.TOKEN_ERROR,
          'Your authentication has expired. Please reconnect your account.'
        );
      }
      return new WealthicaApiError(WealthicaErrorType.API_ERROR, error.message);
    }
    
    // Unknown error format
    return new WealthicaApiError(
      WealthicaErrorType.UNKNOWN_ERROR, 
      typeof error === 'string' ? error : 'Unknown error occurred'
    );
  }
}

// Lazy singleton initialization to avoid noisy constructor logs on import
let _clientInstance: Wealthica2Client | null = null;
const getClientInstance = (): Wealthica2Client => {
  if (!_clientInstance) {
    _clientInstance = new Wealthica2Client();
  }
  return _clientInstance;
};

// Proxy defers construction until first property access
export const wealthica2Client: Wealthica2Client = new Proxy({} as Wealthica2Client, {
  get(_target, prop, _receiver) {
    const inst = getClientInstance();
    // @ts-ignore - dynamic property access
    const value = inst[prop];
    return typeof value === 'function' ? value.bind(inst) : value;
  }
});
